import { Link, useNavigate } from 'react-router-dom';
import { Bell, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useNotificationCount } from '@/hooks/useNotificationCount';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

export const Header = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { count: unreadCount } = useNotificationCount();
  const navigate = useNavigate();

  const borderClass = isDark ? 'border-white/10' : 'border-black/10';
  const textMutedClass = isDark ? 'text-white/60' : 'text-black/60';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 md:hidden pt-safe">
      {/* Main header bar */}
      <div className={`glass border-b ${borderClass}`}>
        <div className="flex items-center justify-between h-14" style={{ paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}>
          {/* Logo — text wordmark */}
          <Link to="/home" data-testid="header-logo" className="flex items-center">
            <span className="sidebar-logo-wordmark" style={{ fontSize: '28px', letterSpacing: '3px' }}>
              BL<span className="sidebar-logo-vx">VX</span>
            </span>
          </Link>

          {/* Right side — Search | Bell | Avatar */}
          <div className="flex items-center gap-1">
            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              className={textMutedClass}
              onClick={() => navigate('/explore')}
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Notifications */}
            <Button
              variant="ghost"
              size="icon"
              className={`${textMutedClass} relative`}
              onClick={() => navigate('/notifications')}
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  background: '#D44F2E',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 700,
                  minWidth: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            {/* Avatar → Profile */}
            {user && (
              <button
                onClick={() => navigate(`/profile/${user.username || 'me'}`)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                aria-label="Profile"
                data-testid="header-avatar-btn"
              >
                <Avatar className="h-8 w-8" style={{ border: '1.5px solid rgba(240,123,29,0.4)' }}>
                  <AvatarImage
                    src={user.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${user.name || 'U'}&backgroundColor=1a1a1a&textColor=ffffff`}
                    alt={user.name || 'User'}
                  />
                  <AvatarFallback style={{ background: 'linear-gradient(135deg,#F07B1D,#C85A12)', color: '#050608', fontFamily: "'Bebas Neue', sans-serif", fontSize: '13px' }}>
                    {(user.name || user.username || 'U').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Fire accent bar */}
      <div className="h-0.5" style={{ background: 'linear-gradient(90deg, transparent, var(--fire, #F07B1D), transparent)' }} />
    </header>
  );
};
